import { COMPANY_TITLE, TimeZone } from 'config'
import moment from 'moment-timezone'
import { getPrice2decimal, getPrice3decimal, removeComma } from 'utils'

import { TermSheetPlaceholders } from '../LoanTermSheet/constants'
import { validatePlaceholders } from '../LoanTermSheet/logic'

/** Legal name baked into templates before portals had their own company title. */
const LEGACY_COMPANY_NAME = 'Nextres, LLC'

export const ReviewFormPlaceholders = ['CompanyName', ...[...TermSheetPlaceholders].sort()]

function hasValue(value: unknown) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

/** Values substituted into valuation review title and description. CompanyName is this portal's legal name. */
export function reviewFormPlaceholderValues(loan?: Record<string, any> | null): Record<string, string> {
  const company = COMPANY_TITLE || ''
  const values: Record<string, string> = {
    CompanyName: company,
    LenderCompany: company,
    TodayDate: moment().tz(TimeZone).format('MM/DD/YYYY'),
    FiveLaterDate: moment().tz(TimeZone).add(5, 'days').format('MM/DD/YYYY'),
  }
  if (!loan) return values

  const borrowerName = `${loan.borrowerFirstName || ''} ${loan.borrowerLastName || ''}`.trim()
  if (borrowerName) values.BorrowerName = borrowerName
  if (hasValue(loan.entityTitle)) {
    values.BorrowerCompanyName = String(loan.entityTitle)
    values.BorrowerEntityTitle = String(loan.entityTitle)
  }
  if (hasValue(loan.subjectPropertyAddress)) values.SubjectPropertyAddress = String(loan.subjectPropertyAddress)
  if (hasValue(loan.proposedLoanAmount)) {
    const amount = removeComma(loan.proposedLoanAmount)
    values.LoanAmount = getPrice3decimal(amount)
    values['120%LoanAmount'] = getPrice3decimal(amount * 1.2)
  }
  if (hasValue(loan.loanTerm)) values.LoanTerm = `${removeComma(loan.loanTerm)}`
  if (hasValue(loan.borrowerType)) values.BorrowerType = String(loan.borrowerType)
  if (hasValue(loan.estimatedCreditScore)) values.EstimatedCreditScore = `${loan.estimatedCreditScore}`
  if (hasValue(loan.amortizationType)) values.AmortizationType = String(loan.amortizationType)
  if (hasValue(loan.asIsValue)) values.AsIsValue = `${getPrice2decimal(loan.asIsValue, false, true)}`
  if (hasValue(loan.propertyPurchasePrice)) values.PurchasePrice = getPrice3decimal(loan.propertyPurchasePrice)

  return values
}

export function fillReviewFormText(text: string | undefined | null, values: Record<string, string>): string {
  if (!text) return ''
  let out = String(text)
  if (out.includes('{{')) {
    out = out.replace(/\{\{(.*?)\}\}/g, (match, raw) => {
      const name = String(raw).trim()
      if (Object.prototype.hasOwnProperty.call(values, name)) return values[name] ?? ''
      return match
    })
  }
  const company = values.CompanyName || ''
  if (company && company !== LEGACY_COMPANY_NAME && out.includes(LEGACY_COMPANY_NAME)) {
    out = out.split(LEGACY_COMPANY_NAME).join(company)
  }
  return out
}

export function reviewFormPlaceholderError(value: string) {
  const { hasError, errorPlaceholders } = validatePlaceholders(value || '', ['CompanyName'])
  if (!hasError) return ''
  if (errorPlaceholders.length) return `Invalid placeholder: ${errorPlaceholders.join(', ')}`
  return 'Invalid placeholders exist'
}
